import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "./PageTitleCompoent";
import "../css/AddDoctorScreen.css";
import avator from "../assets/Avator.png";
import api from "../api";

/* ✅ Moved outside so typing doesn't reset input focus */
const FormInput = ({
  label,
  field,
  type = "text",
  required = false,
  maxLength,
  allowOnly,
  formData,
  handleInputChange,
  errors,
  fieldRefs,
}) => (
  <div className="add-doctor-form-group">
    <label className="add-doctor-label">
      {label} {required && <span className="required-indicator">*</span>}
    </label>
    <input
      ref={fieldRefs[field]}
      type={type}
      placeholder={`Enter ${label}...`}
      className="add-doctor-input"
      value={formData[field]}
      onChange={(e) => {
        let value = e.target.value;
        if (allowOnly) value = value.replace(allowOnly, "").slice(0, maxLength);
        handleInputChange(field, value);
      }}
    />
    {errors[field] && (
      <div className="add-doctor-error-message">{errors[field]}</div>
    )}
  </div>
);

const FileUploadSection = ({
  id,
  label,
  category,
  files,
  documentInputRef,
  handleFileChange,
  handleRemove,
}) => (
  <div className="add-doctor-upload-row">
    <label className="add-doctor-label-upload" htmlFor={id}>
      {label}
    </label>
    <div className="add-doctor-upload-section">
      <label htmlFor={id} className="add-doctor-file-upload-uniform">
        <span className="add-doctor-upload-text-uniform">Choose File</span>
        <input
          type="file"
          id={id}
          ref={documentInputRef}
          className="add-doctor-upload-input"
          multiple
          onChange={(e) => handleFileChange(e, category)}
        />
      </label>
      <div className="uploaded-files">
        {files[category]?.map((file, index) => (
          <div className="file-preview-wrapper" key={file.id}>
            <span className="file-label">File {index + 1}</span>
            <div className="file-preview-row">
              <div className="file-preview">
                <div className="file-info-box">
                  <img
                    src={file.preview}
                    alt="preview"
                    className="file-icon"
                  />
                  <div className="file-text">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">{file.size}</span>
                  </div>
                </div>
              </div>
              <button
                className="file-remove"
                onClick={() => handleRemove(file.id, category)}
                title="Remove File"
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 
                    .825-.587 1.413T17 21zm2-4h2V8H9zm4 
                    0h2V8h-2z"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function AddDoctorScreen() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    phone: "",
    gstn: "",
    email: "",
    address: "",
    hospital: "",
    bio: "",
    perVisitFee: "",
    commission: "",
    overallFee: "",
  });
  const [files, setFiles] = useState({
    idProof: [],
    addressProof: [],
    business: [],
    service: [],
    passport: [],
    bank: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [profileImg, setProfileImg] = useState(avator);
  const [profileFile, setProfileFile] = useState(null);

  const profileInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const fieldRefs = {
    name: useRef(null),
    designation: useRef(null),
    phone: useRef(null),
    gstn: useRef(null),
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "email" && value) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setErrors((prev) => ({
        ...prev,
        email: emailPattern.test(value)
          ? undefined
          : "Please enter a valid email address",
      }));
    } else if (field === "email") {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handleSaveDoctor = async (e) => {
    e.preventDefault();

    const validationErrors = {};
    if (!formData.name.trim()) validationErrors.name = "Please enter name";
    if (!formData.designation.trim())
      validationErrors.designation = "Please enter designation";
    if (!formData.phone.trim())
      validationErrors.phone = "Please enter phone number";
    if (!formData.gstn.trim()) validationErrors.gstn = "Please enter GSTN number";

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const firstError = Object.keys(validationErrors)[0];
      fieldRefs[firstError]?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    try {
      setLoading(true);
      const data = new FormData();

      data.append("name", formData.name);
      data.append("specialization", formData.designation);
      data.append("gstn", formData.gstn);
      data.append("clinic_name", formData.hospital);
      data.append("phone_number", formData.phone);
      data.append("email", formData.email);
      data.append("address", formData.address);
      data.append("bio", formData.bio);
      data.append("per_visit_fee", formData.perVisitFee || 0);
      data.append("commission_percentage", formData.commission || 0);
      data.append("overall_fee", formData.overallFee || 0);

      if (profileFile) data.append("profile_image", profileFile);

      // ✅ FIXED: Correct backend document field mapping
      if (files.idProof.length > 0)
        data.append("id_proof", files.idProof[0].file);
      if (files.addressProof.length > 0)
        data.append("address_proof", files.addressProof[0].file);
      if (files.business.length > 0)
        data.append("business_registration", files.business[0].file);
      if (files.service.length > 0)
        data.append("service_certifications", files.service[0].file);
      if (files.passport.length > 0)
        data.append("passport_photo", files.passport[0].file);
      if (files.bank.length > 0)
        data.append("bank_details", files.bank[0].file);

      await api.post("doctor-profiles/add/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Doctor saved successfully!");
      navigate(-1);
    } catch (error) {
      console.error("Error saving doctor:", error);
      alert("Failed to save doctor. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImg(URL.createObjectURL(file));
      setProfileFile(file);
    }
  };

  const handleFileChange = (e, category) => {
    const selectedFiles = Array.from(e.target.files).map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: (file.size / (1024 * 1024)).toFixed(2) + "MB",
      preview: URL.createObjectURL(file),
      file,
    }));
    setFiles((prev) => ({
      ...prev,
      [category]: [...prev[category], ...selectedFiles],
    }));
  };

  const handleRemove = (id, category) => {
    setFiles((prev) => ({
      ...prev,
      [category]: prev[category].filter((file) => file.id !== id),
    }));
  };

  const uploadSections = [
    { id: "id-proof-upload", label: "ID Proof", category: "idProof" },
    { id: "address-proof-upload", label: "Address Proof", category: "addressProof" },
    { id: "business-upload", label: "Business Registration", category: "business" },
    { id: "service-upload", label: "Service Certifications", category: "service" },
    { id: "passport-upload", label: "Passport Photo", category: "passport" },
    { id: "bank-upload", label: "Bank Details", category: "bank" },
  ];

  return (
    <div className="add-doctor-container">
      <Sidebar />
      <div className="add-doctor-main">
        <Header />
        <div className="add-doctor-content">
          <div className="add-doctor-heading-row">
            <PageTitle title="Add Doctors" onBack={() => navigate(-1)} />
          </div>
          <div className="add-doctor-card">
            <div className="add-doctor-card-header">
              <div className="add-doctor-profile-section">
                <h2>Add Profile</h2>
                <div className="add-doctor-profile-image-wrapper">
                  <img
                    src={profileImg}
                    alt="Profile"
                    className="add-doctor-profile-image"
                  />
                </div>
                <button
                  className="add-doctor-upload-btn"
                  onClick={() => profileInputRef.current.click()}
                  type="button"
                >
                  <span className="upload-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    >
                      <path d="m7 8 5-5 5 5M12 15V3M22 15v4a2 2 0 0 1-2 
                      2H4a2 2 0 0 1-2-2v-4" />
                    </svg>
                  </span>
                  <span className="upload-text">Upload</span>
                </button>
                <input
                  type="file"
                  ref={profileInputRef}
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleProfileUpload}
                />
              </div>
            </div>

            <form className="add-doctor-doctor-form">
              {/* ✅ Your full existing JSX form kept 100% unchanged */}
              <div className="add-doctor-form-row">
                <FormInput
                  label="Add Name"
                  field="name"
                  required
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  fieldRefs={fieldRefs}
                />
                <FormInput
                  label="Add Designation"
                  field="designation"
                  required
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  fieldRefs={fieldRefs}
                />
              </div>

              <div className="add-doctor-form-row">
                <FormInput
                  label="Add Phone Number"
                  field="phone"
                  required
                  maxLength={10}
                  allowOnly={/[^0-9]/g}
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  fieldRefs={fieldRefs}
                />
                <FormInput
                  label="Add Mail ID"
                  field="email"
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  fieldRefs={fieldRefs}
                />
              </div>

              <div className="add-doctor-form-row">
                <FormInput
                  label="Add Address"
                  field="address"
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  fieldRefs={fieldRefs}
                />
                <FormInput
                  label="Add GSTN Number"
                  field="gstn"
                  required
                  maxLength={15}
                  allowOnly={/[^0-9A-Z]/g}
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  fieldRefs={fieldRefs}
                />
              </div>

              <div className="add-doctor-form-row">
                <FormInput
                  label="Add Hospital Name"
                  field="hospital"
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  fieldRefs={fieldRefs}
                />
              </div>

              <div className="add-doctor-form-group full-width-group">
                <label className="add-doctor-label">About Doctor</label>
                <textarea
                  className="about-doctor-textarea"
                  rows={6}
                  value={formData.bio}
                  onChange={(e) => handleInputChange("bio", e.target.value)}
                />
              </div>

              <div className="add-doctor-form-row consultant-fees-section">
                <div className="add-doctor-form-group full-width-group">
                  <label className="add-doctor-label">
                    Add Consultant Fees
                  </label>
                  {[
                    {
                      label: "Per Visit Consultant Fees",
                      field: "perVisitFee",
                      symbol: "₹",
                    },
                    {
                      label: "Pawfriends Commission%",
                      field: "commission",
                      symbol: "%",
                    },
                    {
                      label: "Overall Fees",
                      field: "overallFee",
                      symbol: "₹",
                    },
                  ].map(({ label, field, symbol }) => (
                    <div className="consultant-fee-row" key={field}>
                      <label className="fee-label">{label}</label>
                      <div className="fee-input-wrapper">
                        <span className="fee-symbol">{symbol}</span>
                        <input
                          type="number"
                          className="fee-input"
                          value={formData[field]}
                          onChange={(e) =>
                            handleInputChange(field, e.target.value)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {uploadSections.map((section) => (
                <FileUploadSection
                  key={section.id}
                  {...section}
                  files={files}
                  documentInputRef={documentInputRef}
                  handleFileChange={handleFileChange}
                  handleRemove={handleRemove}
                />
              ))}

              <div className="save-button-wrapper">
                <button
                  className="add-doctor-Save-btn"
                  onClick={handleSaveDoctor}
                  type="button"
                  disabled={loading}
                >
                  <span className="add-doctor-Save-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M19 13v5c0 .55-.45 1-1 1H6c-.55 
                        0-1-.45-1-1v-5c0-.55-.45-1-1-1s-1 
                        .45-1 1v6c0 1.1.9 2 2 
                        2h14c1.1 0 2-.9 2-2v-6c0-.55-.45-1-1-1s-1 
                        .45-1 1m-6-.33l1.88-1.88a.996.996 0 
                        1 1 1.41 1.41l-3.59 
                        3.59a.996.996 0 0 1-1.41 0L7.7 
                        12.2a.996.996 0 1 1 
                        1.41-1.41L11 12.67V4c0-.55.45-1 
                        1-1s1 .45 1 1z"
                      />
                    </svg>
                  </span>
                  <span className="add-doctor-Save-text">
                    {loading ? "Saving..." : "Save"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
